// POS Terminal — per-location Stripe Terminal readers.
// Requires Stripe onboarding at the installer level first.
import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { HTTPService } from '../../../../services/httpService';

const API = new HTTPService();

class POSTerminalTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      terminalLoading: true,
      terminalLocation: null,
      readers: [],
      splashScreen: null,
      registeringLocation: false,

      showPairModal: false,
      pairCode: '',
      pairLabel: '',
      pairLoading: false,
      pairError: null,

      removingReaderId: null
    };
  }

  componentDidMount() {
    this.loadTerminalSetup();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.location?.location_id;
    const nextId = this.props.location?.location_id;
    if (prevId !== nextId) {
      this.loadTerminalSetup();
    }
  }

  loadTerminalSetup = async () => {
    const locationId = this.props.location?.location_id;
    if (!locationId) {
      this.setState({ terminalLoading: false, terminalLocation: null, readers: [], splashScreen: null });
      return;
    }
    if (!this.props.installer?.onboarding_complete) {
      this.setState({ terminalLoading: false });
      return;
    }

    try {
      this.setState({ terminalLoading: true });
      const response = await API.get(`/terminal/location/${locationId}/setup`);
      const data = response.data;
      this.setState({
        terminalLocation: data.terminal_location || null,
        readers: data.readers || [],
        splashScreen: data.splash_screen || null,
        terminalLoading: false
      });
    } catch (error) {
      console.error('Error loading terminal setup:', error);
      this.setState({ terminalLoading: false });
    }
  };

  handleRegisterLocation = async () => {
    const locationId = this.props.location?.location_id;
    try {
      this.setState({ registeringLocation: true });
      const response = await API.post(`/terminal/location/${locationId}/register`);
      this.setState({
        terminalLocation: response.data.stripe_terminal_location_id,
        registeringLocation: false
      });
      toast.success('POS terminal enabled for this location!');
    } catch (error) {
      console.error('Error registering terminal location:', error);
      toast.error(error.response?.data?.message || 'Failed to enable POS terminal.');
      this.setState({ registeringLocation: false });
    }
  };

  handleOpenPairModal = () => {
    this.setState({ showPairModal: true, pairCode: '', pairLabel: '', pairError: null });
  };

  handleClosePairModal = () => {
    this.setState({ showPairModal: false, pairCode: '', pairLabel: '', pairError: null });
  };

  handlePairTerminal = async () => {
    const locationId = this.props.location?.location_id;
    const { pairCode, pairLabel } = this.state;

    const code = pairCode.trim().toLowerCase().replace(/\s+/g, '-');
    if (!code) {
      this.setState({ pairError: 'Please enter the pairing code displayed on the terminal.' });
      return;
    }

    try {
      this.setState({ pairLoading: true, pairError: null });
      const body = { registration_code: code };
      if (pairLabel.trim()) body.label = pairLabel.trim();

      const response = await API.post(`/terminal/location/${locationId}/reader`, body);
      const newReader = response.data.reader;

      this.setState(prev => ({
        readers: [...prev.readers, newReader],
        showPairModal: false,
        pairCode: '',
        pairLabel: '',
        pairLoading: false
      }));
      toast.success(`Terminal "${newReader.label}" paired successfully!`);
    } catch (error) {
      console.error('Error pairing terminal:', error);
      this.setState({
        pairError: error.response?.data?.message || 'Failed to pair terminal. Check the code and try again.',
        pairLoading: false
      });
    }
  };

  handleRemoveReader = async (readerId, label) => {
    if (!window.confirm(`Remove terminal "${label}"? It will need to be re-paired to use again.`)) return;

    try {
      this.setState({ removingReaderId: readerId });
      await API.delete(`/terminal/reader/${readerId}`);
      this.setState(prev => ({
        readers: prev.readers.filter(r => r.id !== readerId),
        removingReaderId: null
      }));
      toast.success('Terminal removed.');
    } catch (error) {
      console.error('Error removing reader:', error);
      toast.error('Failed to remove terminal.');
      this.setState({ removingReaderId: null });
    }
  };

  renderStripeGate = () => (
    <div className="card mb-4">
      <div className="card-header">
        <h4 className="mb-0">
          <i className="fa-sharp fa-regular fa-credit-card-front mr-2"></i>
          POS Terminal
        </h4>
      </div>
      <div className="card-body text-center py-4">
        <i className="fa-sharp fa-regular fa-lock fa-2x text-muted mb-3 d-block" style={{ opacity: 0.5 }}></i>
        <p className="mb-2"><strong>Finish Payment Processor setup first</strong></p>
        <p className="text-muted mb-0">
          POS terminals use your Stripe account for in-person payments. Complete Stripe onboarding on the
          Payments &amp; Accounting tab (switch to All Locations via the header selector on multi-location
          accounts) before pairing a reader.
        </p>
      </div>
    </div>
  );

  render() {
    const { installer, location } = this.props;
    const {
      terminalLoading, terminalLocation, readers, registeringLocation,
      showPairModal, pairCode, pairLabel, pairLoading, pairError, removingReaderId
    } = this.state;

    if (!installer?.onboarding_complete) {
      return this.renderStripeGate();
    }

    if (!location) {
      return (
        <div className="card mb-4">
          <div className="card-body text-muted">No location available.</div>
        </div>
      );
    }

    return (
      <>
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="fa-sharp fa-regular fa-credit-card-front mr-2"></i>
              POS Terminal
            </h4>
            {terminalLocation && (
              <div>
                <button
                  className="btn btn-outline-secondary btn-sm mr-2"
                  onClick={this.loadTerminalSetup}
                  disabled={terminalLoading}
                >
                  <i className="fa-sharp fa-regular fa-arrows-rotate mr-1"></i> Refresh
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={this.handleOpenPairModal}
                >
                  <i className="fa-sharp fa-regular fa-plus mr-1"></i> Pair Terminal
                </button>
              </div>
            )}
          </div>
          <div className="card-body">
            {terminalLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="text-muted mt-2 mb-0">Loading terminal status...</p>
              </div>
            ) : !terminalLocation ? (
              <div className="text-center py-4">
                <i className="fa-sharp fa-regular fa-credit-card-front fa-3x text-muted mb-3 d-block" style={{ opacity: 0.4 }}></i>
                <p className="text-muted mb-3">
                  Enable POS terminals for this location to accept in-person card payments.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={this.handleRegisterLocation}
                  disabled={registeringLocation}
                >
                  {registeringLocation ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                      Enabling...
                    </>
                  ) : (
                    <>
                      <i className="fa-sharp fa-regular fa-bolt mr-2"></i>
                      Enable POS Terminals
                    </>
                  )}
                </button>
              </div>
            ) : (
              <>
                {readers.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-3">
                      No terminals paired yet. Power on your WisePOS E reader, connect it to WiFi, and click "Pair Terminal" to get started.
                    </p>
                  </div>
                ) : (
                  <div className="row">
                    {readers.map(reader => (
                      <div className="col-md-6 mb-3" key={reader.id}>
                        <div className="card bg-light">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <h6 className="mb-1">{reader.label || 'Terminal'}</h6>
                                <small className="text-muted">{reader.device_type === 'bbpos_wisepos_e' ? 'BBPOS WisePOS E' : reader.device_type}</small>
                              </div>
                              <span className={`badge ${reader.status === 'online' ? 'badge-success' : 'badge-secondary'}`}>
                                {reader.status === 'online' ? 'Online' : 'Offline'}
                              </span>
                            </div>
                            {reader.serial_number && (
                              <div className="mt-2">
                                <small className="text-muted">S/N: {reader.serial_number}</small>
                              </div>
                            )}
                            <div className="mt-2 text-right">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => this.handleRemoveReader(reader.id, reader.label)}
                                disabled={removingReaderId === reader.id}
                              >
                                {removingReaderId === reader.id ? 'Removing...' : 'Remove'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Pair Terminal Modal */}
        <Modal show={showPairModal} onHide={this.handleClosePairModal}>
          <Modal.Header closeButton>
            <Modal.Title>Pair Terminal</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-4">
              Enter the pairing code displayed on your WisePOS E terminal screen. Make sure the device is powered on and connected to WiFi.
            </p>

            {pairError && (
              <div className="alert alert-danger">{pairError}</div>
            )}

            <div className="form-group">
              <label className="font-weight-bold">Pairing Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., simulated-wpe-code"
                value={pairCode}
                onChange={(e) => this.setState({ pairCode: e.target.value })}
                disabled={pairLoading}
                autoFocus
              />
              <small className="text-muted">
                This is the word phrase shown on the terminal's screen when it's ready to pair.
              </small>
            </div>

            <div className="form-group mb-0">
              <label className="font-weight-bold">Label <span className="text-muted font-weight-normal">(optional)</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Front Desk, Bay 1, Mobile Van"
                value={pairLabel}
                onChange={(e) => this.setState({ pairLabel: e.target.value })}
                disabled={pairLoading}
              />
              <small className="text-muted">
                A friendly name to identify this terminal.
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleClosePairModal} disabled={pairLoading}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.handlePairTerminal} disabled={pairLoading || !pairCode.trim()}>
              {pairLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                  Pairing...
                </>
              ) : (
                'Pair Terminal'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}

export default POSTerminalTab;
